import React, { useState, useEffect } from "react";
import { TextField, Button, InputAdornment, IconButton } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import EyeIcon from "@styles/all-images/icons/eye-icon.svg";
import EyeOffIcon from "@styles/all-images/icons/eye-off-icon.svg";
import actionsHandler from "@/store/actions/actionHandler";
import { RootState } from "@/store";
import {
  setLoading,
  setAuth,
  setTempEmail,
  setOtpSent,
  clearTempData,
  setTempToken,
  setTempUser,
} from "@/store/slices/authSlice";
import { AppDispatch } from "@/store";
import { useTranslation } from "react-i18next";
import { validationSchema } from "./validationSchema";

const { unAuthenticatedCall: loginMethod } = actionsHandler("signIn");

const SignIn = () => {
  const { t } = useTranslation("auth"); // Specify the 'auth' namespace
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, isLocked, lockExpiry } = useSelector(
    (state: RootState) => state.auth,
  );

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(setLoading(true));
        const response = await dispatch(
          loginMethod({
            payload: values,
            method: "post",
          }),
        );

        if (response.ok) {
          const user = {
            id: response.data.id,
            email: response.data.email,
            phone: response.data.phone,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            is_active: response.data.is_active,
            is_staff: response.data.is_staff,
            user_type: response.data.user_type,
          };

          dispatch(setTempUser(user));
          dispatch(setTempEmail(values.email));
          dispatch(setTempToken(response.data.token));
          dispatch(setOtpSent(true));
          navigate("/phone-verification");
          toast.success(response.data.message || t("signIn.messages.otpSent"));
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error(t("signIn.messages.unexpectedError"));
      } finally {
        dispatch(setLoading(false));
      }
    },
  });

  useEffect(() => {
    if (isLocked && lockExpiry) {
      const remainingTime = Math.ceil((lockExpiry - Date.now()) / (60 * 1000));
      toast.error(
        t("signIn.messages.accountLocked", { minutes: remainingTime }),
      );
    }
  }, [isLocked, lockExpiry]);

  useEffect(() => {
    dispatch(clearTempData());
  }, []);

  return (
    <div className="tw-min-h-screen tw-flex">
      {/* Left Section: Background Image */}
      <div className="lg:tw-w-3/4 md:tw-w-2/4 sm:tw-w-full tw-hidden login-bg tw-bg-cover tw-bg-center sm:tw-flex tw-items-end tw-justify-start tw-text-white tw-p-14">
        <div className="tw-px-4">
          <h1 className="tw-font-bold font-size-5xl tw-mb-4 tw-leading-[74px]">
            Innovative Collateral & <br /> Risk Solutions
          </h1>
        </div>
      </div>

      {/* Right Section: Login Form */}
      <div className="lg:tw-w-1/3 md:tw-w-2/4 sm:tw-w-full tw-w-full tw-flex tw-items-center tw-justify-center">
        <div className="tw-w-full tw-max-w-md tw-px-16">
          <div className="tw-mb-8">
            <img
              src="/logo/Logo.svg"
              alt="Logo"
              className="tw-mx-auto tw-max-w-[150px] tw-mb-14"
            />
            <h5 className="tw-text-[22px] font-primary font-primary-color font-weight-semibold tw-leading-6">
              {t("signIn.title")}
            </h5>
            <p className="tw-mt-2 tw-leading-6 font-size-sm font-gray-color font-weight-medium">
              {t("signIn.subtitle")}
            </p>
          </div>

          <form onSubmit={formik.handleSubmit}>
            {/* Email Input */}
            <div className="form-group">
              <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                {t("signIn.form.email.label")}
              </p>
              <TextField
                fullWidth
                type="email"
                name="email"
                variant="outlined"
                placeholder={t("signIn.form.email.placeholder")}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                className="tw-mb-4"
              />
              {formik.touched.email && formik.errors.email && (
                <p className="tw-text-red-500 tw-text-sm tw-mt-1 tw-ml-2">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div className="form-group tw-mt-6">
              <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                {t("signIn.form.password.label")}
              </p>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                placeholder={t("signIn.form.password.placeholder")}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                className="tw-mb-4"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePassword}
                        edge="end"
                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                      >
                        {showPassword ? (
                          <img src={EyeIcon} alt="EyeIcon" />
                        ) : (
                          <img src={EyeOffIcon} alt="EyeOffIcon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.password && formik.errors.password && (
                <p className="tw-text-red-500 tw-text-sm tw-mt-1 tw-ml-2">
                  {formik.errors.password}
                </p>
              )}
            </div>

            {/* Forgot Password */}
            <div className="tw-flex tw-items-center tw-justify-end tw-mb-6">
              <a
                href="/forget-password"
                className="tw-text-sm tw-font-medium tw-leading-6 tw-text-[#146C94]"
              >
                {t("signIn.form.forgotPassword")}
              </a>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || isLocked}
              className="tw-py-2 primary-custom-button"
            >
              {loading
                ? t("signIn.form.button.loading")
                : t("signIn.form.button.default")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
